import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/buttons';
import { Modals } from '@/helpers/modals';
import { useNavigation } from '@/hooks/navigation';
import { useGetCommonStore, useModalsStore } from '@/store';
import { ComparisonSlider } from './components/ComparisonSlider';
import { ComparisonTable } from './components/ComparisonTable';
import { useComparisonData } from './hooks/useComparisonData';
import { ScButtonWrapper, ScComparisonSection, ScTitle } from './styled';

type AuthState = 'guest' | 'auth';
type ButtonVariant = { primary?: boolean; whiteTransparent?: boolean };

const BUTTON_VARIANTS: Record<AuthState, ButtonVariant> = {
  guest: { primary: true },
  auth: { whiteTransparent: true },
};

const BUTTON_LABEL_KEYS: Record<AuthState, string> = {
  guest: 'button.signUp',
  auth: 'button.moreDetails',
};

export const ComparisonSection = memo(() => {
  const { t } = useTranslation('common');
  const { title, colsTitle, cols, sections } = useComparisonData();
  const useCommonStore = useGetCommonStore();

  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const open = useModalsStore.use.open();
  const { navigate } = useNavigation();

  const handleBtnClick = useCallback(() => {
    if (isAuthenticated) return navigate('/loyalty', true);
    return open(Modals.Registration);
  }, [isAuthenticated, navigate, open]);

  const authState: AuthState = isAuthenticated ? 'auth' : 'guest';
  const buttonProps = {
    ...BUTTON_VARIANTS[authState],
    children: t(BUTTON_LABEL_KEYS[authState]),
  };

  return (
    <ScComparisonSection>
      <ScTitle>{title}</ScTitle>

      <div className="slider-wrapper">
        <ComparisonSlider
          cols={cols}
          colsTitle={colsTitle}
          sections={sections}
        />
      </div>
      <div className="table-wrapper">
        <ComparisonTable
          cols={cols}
          colsTitle={colsTitle}
          sections={sections}
        />
      </div>

      <ScButtonWrapper>
        <Button onClick={handleBtnClick} wide {...buttonProps} />
      </ScButtonWrapper>
    </ScComparisonSection>
  );
});

ComparisonSection.displayName = 'ComparisonSection';
